import moment from 'moment';
import type {
  IBeneficialOwnershipVisualData,
  IVisualKpi,
  IVisualLabels,
  IVisualNetworkGroup,
  IVisualNetworkItem,
  VisualTone,
} from 'utils/beneficialOwnershipVisualData';
import { RiskLevel } from './entityRiskEvaluation';
import { EntityRiskReport, RiskReportFinding } from './entityRiskReport';

// The one-page visual summary of an Export and Transshipment Risk run, drawn
// by the Beneficial Ownership visual (components/Common/
// BeneficialOwnershipVisualPdf) on the same dashboard theme. Everything is
// read from the run's report (entityRiskReport), so the visual, the full PDF
// and the page agree: each check is a group on the network map, its findings
// are the nodes, graded by severity, and the high and medium findings make up
// the risk exposure beside it.

const LABELS: Partial<IVisualLabels> = {
  kicker: 'Risk analysis · Export and transshipment risk',
  subtitle:
    'Export control and diversion, adverse media, financial standing and personnel, from live public sources',
  strip: [
    'Subject entity',
    'Screening result',
    'BITE analysis',
    'Report generated',
  ],
  networkTitle: 'Network map',
  networkItem: ['finding', 'findings'],
  networkSource: 'mapped across the checks',
  moreGroup: 'More checks',
  compactGroup: 'Findings',
  exposureTitle: 'Risk exposure',
  exposureItem: ['high or medium finding', 'high or medium findings'],
  exposureNone: 'No high or medium findings',
  exposureList: 'High & medium findings',
  exposureEmpty: 'No high or medium findings came back from the checks.',
  exposureMore: 'more in the network map and report',
  summary: 'Risk drivers',
  footer: 'BITE · Export and Transshipment Risk Intelligence · bitedata.io',
  documentTitle: 'BITE Export and Transshipment Risk Visual Summary',
  documentAuthor: 'BITE Entity Screening',
};

const count = (n: number): string => n.toLocaleString('en-US');

const plural = (n: number, one: string, many: string): string =>
  `${count(n)} ${n === 1 ? one : many}`;

const toneOf = (risk: RiskLevel | null): VisualTone =>
  risk === 'High'
    ? 'high'
    : risk === 'Medium'
    ? 'medium'
    : risk === 'Low'
    ? 'low'
    : 'info';

const SEVERITY_RANK: Record<VisualTone, number> = {
  high: 3,
  medium: 2,
  low: 1,
  info: 0,
};

/** The text up to the end of its first sentence, or all of it when short. */
const firstSentence = (value: string): string => {
  const match = value.match(/^(.+?[.!?])(\s|$)/);
  return match ? match[1] : value;
};

const sourceOf = (finding: RiskReportFinding): string => {
  if (finding.url) {
    const host = finding.url.match(/^https?:\/\/([^/?#]+)/i);
    if (host) return host[1].toLowerCase().replace(/^www\./, '');
  }
  return finding.sourceName;
};

// The detail names the check, since a small run is drawn as one grid of
// findings rather than a group per check.
const nodeOf = (finding: RiskReportFinding): IVisualNetworkItem => ({
  name: firstSentence(finding.claim),
  detail: [
    finding.agentLabel,
    finding.severity || 'Not scored',
    sourceOf(finding),
  ]
    .filter(Boolean)
    .join(' · '),
  flagged: finding.severity === 'High',
  tone: toneOf(finding.severity),
});

/** The sites the run cites most, e.g. "reuters.com, ft.com, +4". */
const sourceSummary = (report: EntityRiskReport): string => {
  const domains = report.sources.map((source) => source.domain).filter(Boolean);
  if (!domains.length) {
    return report.sources.length ? 'Cited in the full report' : 'None saved';
  }
  const shown = domains.slice(0, 2);
  const rest = domains.length - shown.length;
  return shown.join(', ') + (rest > 0 ? `, +${count(rest)}` : '');
};

const assessmentOf = (
  report: EntityRiskReport,
): IBeneficialOwnershipVisualData['assessment'] => {
  if (!report.counts.answered) {
    return {
      headline: 'No verdict',
      note: 'No check answered, so the entity could not be assessed. Run the checks again.',
      tone: 'medium',
    };
  }
  switch (report.overallRisk) {
    case 'High':
      return {
        headline: 'High risk found',
        note: 'Escalate for compliance review. Enhanced due diligence required.',
        tone: 'high',
      };
    case 'Medium':
      return {
        headline: 'Elevated risk',
        note: 'Some findings need review before proceeding.',
        tone: 'medium',
      };
    case 'Low':
      return {
        headline: 'Low risk',
        note: 'The checks found no high or medium risk for this entity.',
        tone: 'low',
      };
    default:
      return {
        headline: 'Inconclusive',
        note: 'The checks that answered could not score this entity. This is not a clean result; run them again.',
        tone: 'medium',
      };
  }
};

export const buildEntityRiskVisualData = (
  report: EntityRiskReport,
  preparedBy: string,
): IBeneficialOwnershipVisualData => {
  const entityName = report.entityName || 'Unnamed entity';
  const overallTone = toneOf(report.overallRisk);
  const high = report.counts.highFindings;
  const medium = report.findings.filter(
    (finding) => finding.severity === 'Medium',
  ).length;
  // A check that failed or never ran both leave a gap in the verdict.
  const unanswered = report.checks.filter(
    (check) => check.status !== 'answered',
  );

  // ---- The network: each check with findings is a group of them, the most
  // severe groups first.
  const groups: IVisualNetworkGroup[] = report.checks
    .map((check) => {
      const items = check.findingIds
        .map((id) => report.findings.find((finding) => finding.id === id))
        .filter((finding): finding is RiskReportFinding => Boolean(finding))
        .sort(
          (a, b) =>
            SEVERITY_RANK[toneOf(b.severity)] -
            SEVERITY_RANK[toneOf(a.severity)],
        )
        .map(nodeOf);
      return {
        key: 'other' as const,
        // The renderer adds the count: "Export control · 3".
        title: check.label,
        items: items,
        flaggedCount: items.filter((item) => item.flagged).length,
      };
    })
    .filter((group) => group.items.length > 0)
    .sort(
      (a, b) =>
        b.flaggedCount - a.flaggedCount || b.items.length - a.items.length,
    );

  const exposure = report.findings
    .filter(
      (finding) => finding.severity === 'High' || finding.severity === 'Medium',
    )
    .map((finding) => ({
      name: firstSentence(finding.claim),
      detail: [finding.agentLabel, finding.severity, sourceOf(finding)]
        .filter(Boolean)
        .join(' · '),
      flagged: true,
      tone: toneOf(finding.severity),
    }));

  // ---- Counts: a count of nothing is left off, but the checks always show.
  const kpiList: IVisualKpi[] = [
    {
      value: report.counts.answered,
      label: 'Checks answered',
      detail: unanswered.length
        ? `${plural(unanswered.length, 'check', 'checks')} did not answer`
        : `All ${count(report.counts.checks)} returned a result`,
      tone: unanswered.length ? 'medium' : 'info',
    },
    {
      value: report.counts.findings,
      label: 'Key findings',
      detail: high
        ? `${count(high)} rated high risk`
        : medium
        ? `${count(medium)} rated medium risk`
        : 'None rated high or medium',
      tone: 'info',
    },
    {
      value: high,
      label: 'High severity',
      detail: 'Review these first',
      tone: 'high',
    },
    {
      value: report.counts.sources,
      label: 'Sources',
      detail: sourceSummary(report),
      tone: 'info',
    },
  ];
  const kpis = [kpiList[0]].concat(
    kpiList.slice(1).filter((kpi) => kpi.value > 0),
  );

  const checked = moment(report.checkedAt || undefined);
  const drivers = report.checks
    .filter((check) => check.status === 'answered' && check.riskDriver)
    .map((check) => `${check.label}: ${check.riskDriver}.`)
    .join(' ');

  return {
    ticketName: `Export and Transshipment Risk - ${entityName}`,
    entityName: entityName,
    verdict: {
      subject: report.overallRisk
        ? `${report.overallRisk} risk`
        : report.counts.answered
        ? 'Inconclusive'
        : 'No verdict',
      network: high
        ? plural(high, 'high-severity finding', 'high-severity findings')
        : medium
        ? plural(medium, 'medium finding', 'medium findings')
        : 'No high or medium findings',
      tone: overallTone === 'info' ? 'medium' : overallTone,
    },
    subject: {
      title: entityName,
      body: report.country
        ? `Entity record · ${report.country}`
        : 'Entity record · country not stated',
      tone: 'info',
    },
    ownershipResult: {
      title: `${count(report.counts.answered)} of ${count(
        report.counts.checks,
      )} checks answered`,
      body: unanswered.length
        ? `${unanswered
            .map((check) => check.label)
            .join(', ')} did not answer and ${
            unanswered.length === 1 ? 'is' : 'are'
          } left out of the verdict.`
        : 'Every check returned a result.',
      tone: unanswered.length ? 'medium' : 'info',
    },
    analysis: {
      title: 'Live web research · 4 agents',
      body: 'Export control and diversion, adverse media, financial standing and personnel, searched in parallel.',
      tone: 'info',
    },
    report: {
      title: (checked.isValid() ? checked : moment()).format(
        'MMM D, YYYY · HH:mm',
      ),
      body: `Prepared by ${preparedBy || 'BITE Entity Screening'}`,
      tone: 'info',
    },
    kpis: kpis,
    findings: report.findings.map((finding) => ({
      title: [finding.agentLabel, finding.category].filter(Boolean).join(' · '),
      body: finding.claim,
      level: (finding.severity || '').toLowerCase(),
      tone: toneOf(finding.severity),
    })),
    network: {
      badge: {
        label: report.overallRisk ? `${report.overallRisk} risk` : 'No verdict',
        tone: overallTone === 'info' ? 'medium' : overallTone,
      },
      groups: groups,
      total: groups.reduce((sum, group) => sum + group.items.length, 0),
      flagged: high,
    },
    exposure: exposure,
    assessment: assessmentOf(report),
    jurisdictions: [],
    sanctions: drivers,
    labels: LABELS,
  };
};

/** Whether the run has findings to map; without them the visual says nothing. */
export const hasRiskNetworkToMap = (
  data: IBeneficialOwnershipVisualData,
): boolean => data.network.total > 0;

/** Why the visual is unavailable, for the disabled download. */
export const NO_RISK_NETWORK_TO_MAP =
  'No findings to map. Every check that answered came back clean, so download the full report instead.';

export const riskVisualFileName = (report: EntityRiskReport): string => {
  const entity = String(report.entityName || 'Entity')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .trim();
  const checked = moment(report.checkedAt || undefined);
  const date = (checked.isValid() ? checked : moment()).format('MM-DD-YYYY');
  return `Export and Transshipment Risk - ${entity} - ${date} - Visual Summary.pdf`;
};
